import type {
  BalanceResponseBody,
  BillingDisplayRateResponse,
  CreateTopupRequest,
  CreateTopupResponse,
  CreditsResponse,
  GetTopupStatusResponse,
  PricingResponse,
  RedeemGiftCardRequest,
  RedeemGiftCardResponse,
  TransactionsResponse,
  TopupQuoteResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  type TransactionsQuery,
  createTopup,
  getBalance,
  getCredits,
  getDisplayRate,
  getPricing,
  getTopupQuote,
  getTopupStatus,
  getTransactions,
  redeemGiftCard,
} from './billing.ts';

/**
 * React Query wrappers for the tenant billing surface.
 *
 * Query key conventions:
 *   - `['billing', 'balance']`                                  — balance
 *   - `['billing', 'credits']`                                  — credit lots
 *   - `['billing', 'transactions', limit, offset, from, to]`    — ledger page
 *   - `['billing', 'pricing']`                                  — model catalogue
 *
 * Mutation invalidation map:
 *   - redeemGiftCard → invalidate `['billing', 'balance']`,
 *     `['billing', 'credits']` and `['billing', 'transactions', ...]`
 *     (prefix form) so the balance card, the gift breakdown and the
 *     ledger table all reflect the new credit. The dashboard balance
 *     card shares the same `balance` key; re-render is free.
 *
 * `balance` and `credits` are two reads of one fact, so they invalidate
 * together — a balance that moved without its lots moving would show a
 * gift line that no longer adds up.
 */

export const BALANCE_QUERY_KEY = ['billing', 'balance'] as const;
export const CREDITS_QUERY_KEY = ['billing', 'credits'] as const;
export const DISPLAY_RATE_QUERY_KEY = ['billing', 'display-rate'] as const;
export const PRICING_QUERY_KEY = ['billing', 'pricing'] as const;
export const TRANSACTIONS_PREFIX = ['billing', 'transactions'] as const;
export const TOPUP_STATUS_PREFIX = ['billing', 'topup', 'status'] as const;
export const TOPUP_QUOTE_PREFIX = ['billing', 'topup', 'quote'] as const;

/**
 * Poll interval for a topup order waiting on the ZPay callback. 3s is
 * comfortably under the notify-to-poll race window — we want to catch
 * the `paid` transition fast without hammering the API.
 */
export const TOPUP_POLL_INTERVAL_MS = 3_000;

export function transactionsQueryKey(
  query: TransactionsQuery | undefined,
): readonly ['billing', 'transactions', number | undefined, number | undefined, string, string] {
  return [
    'billing',
    'transactions',
    query?.limit,
    query?.offset,
    query?.from ?? '',
    query?.to ?? '',
  ] as const;
}

export function useBalance(
  options?: { readonly enabled?: boolean },
): UseQueryResult<BalanceResponseBody> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: BALANCE_QUERY_KEY,
    queryFn: ({ signal }) => getBalance(fetchImpl, signal),
    enabled: options?.enabled ?? true,
  });
}

/**
 * The credit lots behind the balance. Shares the balance's invalidation
 * triggers — anything that changes the balance changes which lots are
 * outstanding — so `CREDITS_QUERY_KEY` is invalidated wherever
 * `BALANCE_QUERY_KEY` is.
 */
export function useCredits(
  options?: { readonly enabled?: boolean },
): UseQueryResult<CreditsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: CREDITS_QUERY_KEY,
    queryFn: ({ signal }) => getCredits(fetchImpl, undefined, signal),
    enabled: options?.enabled ?? true,
  });
}

export function useDisplayRate(): UseQueryResult<BillingDisplayRateResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: DISPLAY_RATE_QUERY_KEY,
    queryFn: ({ signal }) => getDisplayRate(fetchImpl, signal),
    staleTime: 5 * 60_000,
  });
}

export function useTransactions(query?: TransactionsQuery): UseQueryResult<TransactionsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: transactionsQueryKey(query),
    queryFn: ({ signal }) => getTransactions(fetchImpl, query, signal),
  });
}

export function usePricing(): UseQueryResult<PricingResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: PRICING_QUERY_KEY,
    queryFn: ({ signal }) => getPricing(fetchImpl, signal),
  });
}

export function useRedeemGiftCard(): UseMutationResult<
  RedeemGiftCardResponse,
  Error,
  RedeemGiftCardRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => redeemGiftCard(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BALANCE_QUERY_KEY });
      // A redeemed card writes a new lot; the gift breakdown is stale too.
      void qc.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: TRANSACTIONS_PREFIX });
    },
  });
}

/**
 * Create a topup order. Success does NOT invalidate balance/tx queries
 * — the ledger entry + credit only land after ZPay notify, so we wait
 * for the return page (or the QR modal's poll) to trigger refresh.
 */
export function useCreateTopup(): UseMutationResult<
  CreateTopupResponse,
  Error,
  CreateTopupRequest
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (body) => createTopup(body, fetchImpl),
  });
}

export function useTopupQuote(
  amountCnyCents: number,
  options?: { readonly enabled?: boolean },
): UseQueryResult<TopupQuoteResponse> {
  const { fetchImpl } = useSession();
  const enabled = (options?.enabled ?? true) && amountCnyCents > 0;
  return useQuery({
    queryKey: [...TOPUP_QUOTE_PREFIX, amountCnyCents] as const,
    queryFn: ({ signal }) => getTopupQuote(amountCnyCents, fetchImpl, signal),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Poll a topup order for its settlement status. `enabled` gates the
 * first fetch (useful before the order id is known). `refetchInterval`
 * continues polling only while the server still reports `pending`;
 * any terminal status stops the polling cycle.
 *
 * Caller-owned balance/tx invalidation: when the caller observes a
 * transition from pending → paid they should invalidate
 * `BALANCE_QUERY_KEY` + `TRANSACTIONS_PREFIX` so the post-credit
 * balance + ledger row render.
 */
export function useTopupStatus(
  orderId: string | null,
  options?: { readonly pollIntervalMs?: number; readonly enabled?: boolean },
): UseQueryResult<GetTopupStatusResponse> {
  const { fetchImpl } = useSession();
  const interval = options?.pollIntervalMs ?? TOPUP_POLL_INTERVAL_MS;
  const enabled = (options?.enabled ?? true) && orderId !== null && orderId.length > 0;
  return useQuery({
    queryKey: [...TOPUP_STATUS_PREFIX, orderId] as const,
    queryFn: ({ signal }) => {
      if (orderId === null || orderId.length === 0) {
        throw new Error('orderId is required');
      }
      return getTopupStatus(orderId, fetchImpl, signal);
    },
    enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data === undefined) {
        return interval;
      }
      return data.status === 'pending' ? interval : false;
    },
  });
}
